import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  selectWithcookieData,
  selectUser
} from '../../../redux/WithcookieSlice';
import { useTranslation } from '../../../redux/TranslationHelper';
import '../PayLetterPayment/PayLetterPayment.css';

interface PaymentWerouteSuccessProps {
  className?: string;
  style?: React.CSSProperties;
  isEditor?: boolean;
  componentProps?: {
    title?: string;
    successMessage?: string;
    orderButtonText?: string;
    continueButtonText?: string;
    [key: string]: any;
  };
  data?: {
    title?: string;
    successMessage?: string;
    orderButtonText?: string;
    continueButtonText?: string;
    [key: string]: any;
  };
}

interface WerouteReturnData {
  code?: string;
  message?: string;
  user_id?: string;
  user_name?: string;
  order_no?: string;
  service_name?: string;
  product_name?: string;
  tid?: string;
  cid?: string;
  amount?: number;
  pay_info?: string;
  pgcode?: string;
  transaction_date?: string;
  payhash?: string;
  // Weroute 추가 필드들
  appr_num?: string;        // 승인번호
  card_num?: string;        // 카드번호 (마스킹된)
  installment?: string;     // 할부기간
  issuer?: string;          // 발급사명
  method?: string;          // 결제방식
}

/**
 * Weroute 결제 성공 처리 컴포넌트
 * Weroute에서 결제 완료 후 리턴되는 페이지
 */
const PaymentWerouteSuccess: React.FC<PaymentWerouteSuccessProps> = ({ 
  className = '', 
  style = {}, 
  isEditor = false,
  componentProps = {},
  data = {}
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch() as any;
  
  const user = useSelector(selectUser);
  const withcookieData = useSelector(selectWithcookieData);
  
  const [isLoading, setIsLoading] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState<WerouteReturnData | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // 쿠키에서 값을 읽는 헬퍼 함수
  const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  };

  // 쿠키 삭제 헬퍼 함수
  const deleteCookie = (name: string) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  };

  // 결제 준비 완료 API 호출 함수
  const completeCheckout = async (checkoutId: string, werouteResponse: any) => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${apiBaseUrl}/api/checkouts/${checkoutId}`, {
        method: 'PATCH',
        headers: {
          'Accept': '*/*',
          'Accept-Language': 'ko-KR,ko;q=0.9',
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        credentials: 'include',
        body: JSON.stringify({ 
          status: 'PREPARING',
          werouteResponse: werouteResponse
        })
      });
      
      if (!response.ok) {
        console.error('결제 준비 완료 처리 실패:', response.status);
      } else {
        console.log('결제 준비가 성공적으로 완료되었습니다.');
      }
    } catch (error) {
      console.error('결제 준비 완료 처리 중 오류 발생:', error);
    }
  };
  
  useEffect(() => {
    // 에디터 모드에서는 더미 데이터 사용
    if (isEditor) {
      setPaymentInfo({
        code: '0',
        message: componentProps.successMessage || data.successMessage || '결제가 성공적으로 완료되었습니다.',
        order_no: 'ORDER_DEMO_12345',
        tid: 'TID_DEMO_67890',
        amount: 42000,
        user_name: '홍길동',
        pgcode: 'weroute',
        transaction_date: new Date().toISOString()
      });
      setIsLoading(false);
      return;
    }
    
    const processWerouteSuccess = async () => {
      try {
        // POST로 전달된 데이터는 서버에서 처리하고 
        // 프론트엔드로는 URL 파라미터로 간단한 정보만 전달하는 방식으로 구현
        // (실제 구현 시에는 서버에서 POST 데이터를 받아 처리하고 결과를 전달해야 함)
        
        const searchParams = new URLSearchParams(location.search);
        
        // Weroute 승인 리턴 규격에 따른 파라미터 추출
        const result_cd = searchParams.get('result_cd');
        const result_msg = searchParams.get('result_msg');
        const mid = searchParams.get('mid');
        const tid = searchParams.get('tid');
        const trx_id = searchParams.get('trx_id');
        const amount = searchParams.get('amount');
        const appr_num = searchParams.get('appr_num');
        const item_name = searchParams.get('item_name');
        const acquirer = searchParams.get('acquirer');
        const issuer = searchParams.get('issuer');
        const card_num = searchParams.get('card_num');
        const installment = searchParams.get('installment');
        const buyer_name = searchParams.get('buyer_name');
        const buyer_phone = searchParams.get('buyer_phone');
        const buyer_email = searchParams.get('buyer_email');
        const is_send_email = searchParams.get('is_send_email');
        const method = searchParams.get('method');
        const trx_dttm = searchParams.get('trx_dttm');
        const is_cancel = searchParams.get('is_cancel');
        const temp = searchParams.get('temp');
        
        // 성공 여부 확인 (result_cd가 0000이면 성공, 그 외는 실패)
        if (result_cd === '0000') {
          // temp에서 checkout ID 추출
          let checkoutId = null;
          if (temp) {
            // URL 디코딩
            const decodedTemp = decodeURIComponent(temp);
            if (decodedTemp.includes('checkout_id:')) {
              checkoutId = decodedTemp.split('checkout_id:')[1];
            }
          }
          
          setPaymentInfo({
            code: result_cd,
            message: result_msg || '정상처리되었습니다.',
            order_no: checkoutId ? `#${checkoutId}` : '',
            tid: trx_id || tid || '',
            amount: amount ? Number(amount) : 0,
            user_name: buyer_name || '',
            pgcode: 'weroute',
            transaction_date: trx_dttm || new Date().toISOString(),
            appr_num: appr_num || '',
            card_num: card_num || '',
            installment: installment || '00',
            issuer: issuer || '',
            method: method || ''
          });
          
          // Weroute 응답 데이터 수집
          const werouteResponseData = {
            result_cd,
            result_msg,
            mid,
            tid,
            trx_id,
            amount,
            appr_num,
            item_name,
            acquirer,
            issuer,
            card_num,
            installment,
            buyer_name,
            buyer_phone,
            buyer_email,
            is_send_email,
            method,
            trx_dttm,
            is_cancel,
            temp
          };
          
          // 체크아웃 준비 완료 처리 및 쿠키 정리
          let finalCheckoutId = checkoutId;
          if (!finalCheckoutId) {
            finalCheckoutId = getCookie('weroute_checkout_id');
          }
          
          if (finalCheckoutId) {
            console.log('결제 준비 완료 요청 - checkoutId:', finalCheckoutId);
            console.log('Weroute 응답 데이터:', werouteResponseData);
            completeCheckout(finalCheckoutId, werouteResponseData).finally(() => {
              // 완료 요청 후 쿠키 삭제
              deleteCookie('weroute_checkout_id');
            });
          }
          
          setIsLoading(false);
        } else {
          // 결제 실패 - 실패 페이지로 리다이렉트
          const failParams = new URLSearchParams();
          if (result_cd) failParams.set('result_cd', result_cd);
          if (result_msg) failParams.set('result_msg', result_msg);
          if (temp) failParams.set('temp', temp);
          
          const failUrl = `/payment/weroute/fail?${failParams.toString()}`;
          window.location.href = failUrl;
        }
      } catch (error: any) {
        console.error('Weroute 성공 처리 오류:', error);
        setError(error.message || t('결제 처리 중 오류가 발생했습니다.'));
        setIsLoading(false);
      }
    };
    
    processWerouteSuccess();
  }, [location.search, isEditor]);
  
  const handleGoToOrderHistory = () => {
    navigate('/mypage/orderlist');
  };
  
  const handleGoToHome = () => {
    navigate('/');
  };
  
  if (isLoading) {
    return (
      <div className={`payletter-success-container ${className}`} style={style}>
        <div className="payment-result-card loading">
          <div className="spinner"></div>
          <h2>{t("결제 정보를 처리 중입니다...")}</h2>
          <p>{t("잠시만 기다려주세요.")}</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={`payletter-success-container ${className}`} style={style}>
        <div className="payment-result-card error">
          <div className="error-icon">❌</div>
          <h2>{t("결제 처리 중 오류가 발생했습니다")}</h2>
          <p>{error}</p>
          <div className="payment-action-buttons">
            <button onClick={handleGoToHome} className="go-home-button">
              {t("홈으로 돌아가기")}
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`payletter-success-container ${className}`} style={style}>
      <div className="payment-result-card success">
        <div className="success-icon">✓</div>
        <h2>{componentProps.title || data.title || t("결제가 완료되었습니다")}</h2>
        
        {paymentInfo && (
          <div className="payment-info">
            <div className="info-row">
              <span className="label">{t("주문번호")}:</span>
              <span className="value">{paymentInfo.order_no}</span>
            </div>
            {paymentInfo.tid && (
              <div className="info-row">
                <span className="label">{t("거래번호")}:</span>
                <span className="value">{paymentInfo.tid}</span>
              </div>
            )}
            {paymentInfo.amount && paymentInfo.amount > 0 && (
              <div className="info-row">
                <span className="label">{t("결제금액")}:</span>
                <span className="value">
                  {new Intl.NumberFormat('ko-KR', { 
                    style: 'currency', 
                    currency: 'KRW' 
                  }).format(paymentInfo.amount)}
                </span>
              </div>
            )}
            <div className="info-row">
              <span className="label">{t("결제방법")}:</span>
              <span className="value">
                {paymentInfo.method ? `카드결제 (${paymentInfo.method})` : t("카드결제")}
              </span>
            </div>
            {paymentInfo.appr_num && (
              <div className="info-row">
                <span className="label">{t("승인번호")}:</span>
                <span className="value">{paymentInfo.appr_num}</span>
              </div>
            )}
            {paymentInfo.card_num && (
              <div className="info-row">
                <span className="label">{t("카드번호")}:</span>
                <span className="value">{paymentInfo.card_num}</span>
              </div>
            )}
            {paymentInfo.installment && paymentInfo.installment !== '00' && (
              <div className="info-row">
                <span className="label">{t("할부")}:</span>
                <span className="value">{paymentInfo.installment}개월</span>
              </div>
            )}
            {paymentInfo.issuer && (
              <div className="info-row">
                <span className="label">{t("발급사")}:</span>
                <span className="value">{paymentInfo.issuer}</span>
              </div>
            )}
            {paymentInfo.user_name && (
              <div className="info-row">
                <span className="label">{t("주문자명")}:</span>
                <span className="value">{paymentInfo.user_name}</span>
              </div>
            )}
            {paymentInfo.transaction_date && (
              <div className="info-row">
                <span className="label">{t("결제일시")}:</span>
                <span className="value">
                  {paymentInfo.transaction_date.includes('-') ? 
                    paymentInfo.transaction_date : 
                    new Date(paymentInfo.transaction_date).toLocaleString('ko-KR')
                  }
                </span>
              </div>
            )}
            <div className="info-row">
              <span className="label">{t("결제상태")}:</span>
              <span className="value">{t("결제완료")}</span>
            </div>
          </div>
        )}
        
        <div className="payment-action-buttons">
          <button onClick={handleGoToHome} className="go-home-button">
            {t("홈으로 이동하기")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentWerouteSuccess;

// Named export 추가
export { PaymentWerouteSuccess as PaymentWerouteSuccessComponent };